import React, { useEffect, useMemo, useState } from 'react';
import { SaveOutlined, ReloadOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { adminService, UISettings } from '../../services/admin.service';

/**
 * Configuración global de visibilidad de la UI del cliente.
 *
 * El admin decide qué ve el cliente en la app (kubiy-cloud-ui): items del menú,
 * apps del marketplace, lenguajes/modos de App Deploy, motores de BD y si se
 * ofrecen máquinas virtuales. Se guarda en DynamoDB (GlobalSettings) vía
 * PATCH /admin/settings y el cliente lo lee en GET /products/ui-settings.
 *
 * Semántica: `missing = visible`. Un toggle apagado escribe `false` explícito;
 * encendido escribe `true`. Las opciones de abajo son el catálogo canónico y
 * DEBEN coincidir con los identificadores del cliente:
 *   - menú:        src/router/navigationConfig.tsx
 *   - marketplace: clave `key` de pages/Marketplace/marketplaceCatalog.ts
 *   - appDeploy:   ids de CreateDeployApp.tsx (static/backend project types)
 *   - database:    ids de constants/databaseEngine.ts
 * Si agregas un producto/lenguaje/motor nuevo al cliente, agrégalo aquí.
 */

type Opt = { key: string; label: string };

const MENU_ITEMS: Opt[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'infrastructure', label: 'Infraestructura (menú contenedor)' },
  { key: 'deployApps', label: 'Deploy Apps' },
  { key: 'virtualMachines', label: 'Máquinas Virtuales' },
  { key: 'databases', label: 'Bases de Datos' },
  { key: 'chatbotIA', label: 'Chatbot IA' },
  { key: 'marketplace', label: 'Marketplace' },
  { key: 'orders', label: 'Órdenes' },
];

const MARKETPLACE_PRODUCTS: Opt[] = [
  { key: 'onyx', label: 'Onyx' },
  { key: 'rabbitmq', label: 'RabbitMQ' },
  { key: 'n8n', label: 'n8n' },
  { key: 'strapi', label: 'Strapi' },
  { key: 'medusa', label: 'Medusa' },
  { key: 'odoo', label: 'Odoo' },
  { key: 'idurar', label: 'IDURAR' },
  { key: 'metabase', label: 'Metabase' },
  { key: 'clickhouse', label: 'ClickHouse' },
  { key: 'tooljet', label: 'ToolJet' },
  { key: 'ghost', label: 'Ghost' },
  { key: 'redis', label: 'Redis' },
  { key: 'docker-host', label: 'Docker Host' },
  { key: 'frappe-education', label: 'Frappe Education' },
  { key: 'wordpress', label: 'WordPress' },
  { key: 'sqlserver', label: 'SQL Server Express' },
];

const STATIC_FRAMEWORKS: Opt[] = [
  { key: 'html', label: 'HTML + JavaScript' },
  { key: 'react', label: 'React' },
  { key: 'vue', label: 'Vue.js' },
  { key: 'angular', label: 'Angular' },
  { key: 'vite', label: 'Vite' },
];

const SERVER_RUNTIMES: Opt[] = [
  { key: 'nodejs', label: 'Node.js' },
  { key: 'python', label: 'Python' },
  { key: 'go', label: 'Go' },
  { key: 'netcore', label: '.NET Core' },
  { key: 'nextjs', label: 'Next.js' },
  { key: 'nuxt', label: 'Nuxt.js' },
];

const DB_ENGINES: Opt[] = [
  { key: 'postgresql', label: 'PostgreSQL' },
  { key: 'mysql', label: 'MySQL' },
  { key: 'mariadb', label: 'MariaDB' },
  { key: 'sqlserver', label: 'SQL Server' },
  { key: 'mongodb', label: 'MongoDB' },
];

// Los productos VM (SO) NO son una lista fija: se traen del catálogo real
// (tabla Products, categoría VM) vía /admin/products. Ver vmProducts en el
// componente. La clave de cada bandera es el `id` del producto.

// --- Helpers de lectura/escritura sobre el draft anidado -----------------------

function getFlag(obj: any, path: string[], def = true): boolean {
  let cur = obj;
  for (const k of path) {
    if (cur == null || typeof cur !== 'object' || !(k in cur)) return def;
    cur = cur[k];
  }
  return typeof cur === 'boolean' ? cur : def;
}

function setFlag(obj: any, path: string[], value: boolean): any {
  const [head, ...rest] = path;
  const base = obj && typeof obj === 'object' ? obj : {};
  if (rest.length === 0) {
    return { ...base, [head]: value };
  }
  return { ...base, [head]: setFlag(base[head], rest, value) };
}

// --- Componentes UI ------------------------------------------------------------

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string }> = ({
  checked,
  onChange,
  label,
}) => (
  <label className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer">
    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </label>
);

const Section: React.FC<{ title: string; description?: string; children: React.ReactNode }> = ({
  title,
  description,
  children,
}) => (
  <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-5">
    <div className="mb-3">
      <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
      {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>}
    </div>
    {children}
  </section>
);

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">{children}</div>
);

// --- Página --------------------------------------------------------------------

const AdminSettingsPage: React.FC = () => {
  const [draft, setDraft] = useState<UISettings>({});
  const [version, setVersion] = useState<number>(0);
  const [meta, setMeta] = useState<{ updatedAt?: string | null; updatedBy?: string | null }>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  // Productos VM reales del catálogo (id + nombre); se togglean por id.
  const [vmProducts, setVmProducts] = useState<Opt[]>([]);

  const load = async () => {
    setLoading(true);
    setMessage(null);
    const [res, vmRes] = await Promise.all([
      adminService.getSettings(),
      adminService.getProducts({ category: 'VM' }),
    ]);
    if (res.success && res.settings) {
      setDraft(res.settings);
      setVersion(res.version ?? 0);
      setMeta({ updatedAt: res.updatedAt, updatedBy: res.updatedBy });
    } else {
      setMessage({ type: 'err', text: res.error || 'No se pudo cargar la configuración.' });
    }
    // Los productos VM alimentan la sección de Máquinas Virtuales (data-driven).
    const prods = (vmRes.products || [])
      .map((p) => ({ key: p.id, label: p.name || p.id }))
      .sort((a, b) => a.label.localeCompare(b.label));
    setVmProducts(prods);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const on = (path: string[]) => getFlag(draft, path);
  const toggle = (path: string[]) => (v: boolean) => setDraft((d) => setFlag(d, path, v));

  const save = async () => {
    setSaving(true);
    setMessage(null);
    // Enviamos las 5 secciones completas y explícitas; el backend hace deep-merge.
    const payload: UISettings & { expectedVersion?: number } = {
      menu: { items: Object.fromEntries(MENU_ITEMS.map((o) => [o.key, on(['menu', 'items', o.key])])) },
      marketplace: {
        enabled: on(['marketplace', 'enabled']),
        products: Object.fromEntries(
          MARKETPLACE_PRODUCTS.map((o) => [o.key, on(['marketplace', 'products', o.key])])
        ),
      },
      appDeploy: {
        enabled: on(['appDeploy', 'enabled']),
        static: on(['appDeploy', 'static']),
        server: on(['appDeploy', 'server']),
        staticFrameworks: Object.fromEntries(
          STATIC_FRAMEWORKS.map((o) => [o.key, on(['appDeploy', 'staticFrameworks', o.key])])
        ),
        serverRuntimes: Object.fromEntries(
          SERVER_RUNTIMES.map((o) => [o.key, on(['appDeploy', 'serverRuntimes', o.key])])
        ),
      },
      database: {
        enabled: on(['database', 'enabled']),
        engines: Object.fromEntries(DB_ENGINES.map((o) => [o.key, on(['database', 'engines', o.key])])),
      },
      vm: {
        enabled: on(['vm', 'enabled']),
        products: Object.fromEntries(vmProducts.map((o) => [o.key, on(['vm', 'products', o.key])])),
      },
      expectedVersion: version,
    };

    const res = await adminService.updateSettings(payload);
    if (res.success && res.settings) {
      setDraft(res.settings);
      setVersion(res.version ?? version + 1);
      setMeta({ updatedAt: res.updatedAt, updatedBy: res.updatedBy });
      setMessage({ type: 'ok', text: 'Configuración guardada. El cliente la verá al recargar.' });
    } else {
      setMessage({ type: 'err', text: res.error || 'No se pudo guardar. Recarga y reintenta.' });
    }
    setSaving(false);
  };

  const updatedLabel = useMemo(() => {
    if (!meta.updatedAt) return 'Sin cambios guardados aún (se muestra todo por defecto).';
    const who = meta.updatedBy ? ` por ${meta.updatedBy}` : '';
    return `Última actualización: ${new Date(meta.updatedAt).toLocaleString()}${who} · v${version}`;
  }, [meta, version]);

  const marketplaceEnabled = on(['marketplace', 'enabled']);
  const appDeployEnabled = on(['appDeploy', 'enabled']);
  const databaseEnabled = on(['database', 'enabled']);

  return (
    <div className="max-w-4xl mx-auto px-6 py-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configuración</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Controla qué se muestra a los clientes en la aplicación. Un interruptor apagado oculta esa
            opción; todo lo que no toques se muestra por defecto.
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{updatedLabel}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={load}
            disabled={loading || saving}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2"
          >
            <ReloadOutlined className={loading ? 'animate-spin' : ''} />
            Recargar
          </button>
          <button
            onClick={save}
            disabled={loading || saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            <SaveOutlined />
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`mb-5 px-4 py-3 rounded-lg text-sm flex items-center gap-2 ${
            message.type === 'ok'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
          }`}
        >
          {message.type === 'ok' ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-gray-400">Cargando configuración…</div>
      ) : (
        <>
          {/* Menú principal */}
          <Section title="Menú principal" description="Items visibles en la barra de navegación del cliente.">
            <Grid>
              {MENU_ITEMS.map((o) => (
                <Toggle
                  key={o.key}
                  label={o.label}
                  checked={on(['menu', 'items', o.key])}
                  onChange={toggle(['menu', 'items', o.key])}
                />
              ))}
            </Grid>
          </Section>

          {/* Marketplace */}
          <Section
            title="Marketplace"
            description="Qué apps llave en mano aparecen en el catálogo del marketplace."
          >
            <Toggle
              label="Mostrar el marketplace"
              checked={marketplaceEnabled}
              onChange={toggle(['marketplace', 'enabled'])}
            />
            <div className={`mt-2 ${marketplaceEnabled ? '' : 'opacity-40 pointer-events-none'}`}>
              <Grid>
                {MARKETPLACE_PRODUCTS.map((o) => (
                  <Toggle
                    key={o.key}
                    label={o.label}
                    checked={on(['marketplace', 'products', o.key])}
                    onChange={toggle(['marketplace', 'products', o.key])}
                  />
                ))}
              </Grid>
            </div>
          </Section>

          {/* App Deploy */}
          <Section
            title="App Deploy"
            description="Lenguajes/frameworks y modos (estático o servidor) ofrecidos al crear una app."
          >
            <Toggle
              label="Mostrar App Deploy"
              checked={appDeployEnabled}
              onChange={toggle(['appDeploy', 'enabled'])}
            />
            <div className={`mt-2 ${appDeployEnabled ? '' : 'opacity-40 pointer-events-none'}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400">Estático</h3>
                    <Toggle label="" checked={on(['appDeploy', 'static'])} onChange={toggle(['appDeploy', 'static'])} />
                  </div>
                  <div className={on(['appDeploy', 'static']) ? '' : 'opacity-40 pointer-events-none'}>
                    {STATIC_FRAMEWORKS.map((o) => (
                      <Toggle
                        key={o.key}
                        label={o.label}
                        checked={on(['appDeploy', 'staticFrameworks', o.key])}
                        onChange={toggle(['appDeploy', 'staticFrameworks', o.key])}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400">Servidor / Backend</h3>
                    <Toggle label="" checked={on(['appDeploy', 'server'])} onChange={toggle(['appDeploy', 'server'])} />
                  </div>
                  <div className={on(['appDeploy', 'server']) ? '' : 'opacity-40 pointer-events-none'}>
                    {SERVER_RUNTIMES.map((o) => (
                      <Toggle
                        key={o.key}
                        label={o.label}
                        checked={on(['appDeploy', 'serverRuntimes', o.key])}
                        onChange={toggle(['appDeploy', 'serverRuntimes', o.key])}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* Bases de datos */}
          <Section title="Bases de datos" description="Motores de base de datos ofrecidos al cliente.">
            <Toggle
              label="Mostrar Bases de Datos"
              checked={databaseEnabled}
              onChange={toggle(['database', 'enabled'])}
            />
            <div className={`mt-2 ${databaseEnabled ? '' : 'opacity-40 pointer-events-none'}`}>
              <Grid>
                {DB_ENGINES.map((o) => (
                  <Toggle
                    key={o.key}
                    label={o.label}
                    checked={on(['database', 'engines', o.key])}
                    onChange={toggle(['database', 'engines', o.key])}
                  />
                ))}
              </Grid>
            </div>
          </Section>

          {/* Máquinas virtuales */}
          <Section
            title="Máquinas virtuales"
            description="Renta de VMs (EC2 / Lightsail) y qué sistemas operativos se ofrecen. Los SO vienen del catálogo real (Products)."
          >
            <Toggle
              label="Mostrar Máquinas Virtuales"
              checked={on(['vm', 'enabled'])}
              onChange={toggle(['vm', 'enabled'])}
            />
            <div className={`mt-2 ${on(['vm', 'enabled']) ? '' : 'opacity-40 pointer-events-none'}`}>
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1 px-3">
                Sistemas operativos ({vmProducts.length})
              </h3>
              {vmProducts.length === 0 ? (
                <p className="text-sm text-gray-400 px-3 py-2">
                  No hay productos VM en el catálogo.
                </p>
              ) : (
                <Grid>
                  {vmProducts.map((o) => (
                    <Toggle
                      key={o.key}
                      label={o.label}
                      checked={on(['vm', 'products', o.key])}
                      onChange={toggle(['vm', 'products', o.key])}
                    />
                  ))}
                </Grid>
              )}
            </div>
          </Section>
        </>
      )}
    </div>
  );
};

export default AdminSettingsPage;
